// import type { V2_MetaFunction } from "@remix-run/node";
import { DEFAULT_COUNTRY, DEFAULT_LOGO_URL } from "~/lib/constants/generalConstants";
import { getCanonicalAndAlternates } from "./canonicalTags";
import { getMetaOgTags } from "./metaOgTags";
import { GetDomainName } from "~/helpers/UrlHelper";
import { getCountrySpecificMeta } from "~/components/blocks/Home_Page/constants";
import { getCountryData, getRegionEmail, regionConfigs } from "custom-config/region.configs";
import { getRegionUrlPrefix, propertySlug } from "~/lib/helpers/slugHelper";
import { getFormattedCountryName } from "~/lib/helpers/constants";

const SITE_ORIGIN = "https://www.office-hub.com";
const ORGANIZATION_ID = `${SITE_ORIGIN}/#organization`;
const WEBSITE_ID = `${SITE_ORIGIN}/#website`;
const LEGAL_NAME = "Office Hub Pty Limited";
const HQ_EMAIL = "enquiries.anz@office-hub.com";
const HQ_PHONE = "+61 1300 770 200";
const FEATURED_PROPERTY_LIMIT = 12;

const OFFICE_SERVICES = [
  {
    name: "Serviced Office",
    slug: "serviced-offices",
    description: "A serviced office is a ready-to-use workspace with furniture, utilities, and on-site support included, so a team can move in without setting up the office themselves.",
  },
  {
    name: "Coworking Space",
    slug: "coworking-space",
    description: "A coworking space is an open-plan desk in a serviced office. A dedicated desk stays yours to personalize, while a hot desk is unassigned and cleared at the end of the day. Working alongside other people makes daily collaboration easier.",
  },
  {
    name: "Enterprise Office",
    slug: "enterprise-office-space",
    description: "An enterprise office is a furnished workspace for larger teams. It adapts as the team changes size, unlike a fixed conventional lease, and typically includes high-speed internet, meeting rooms, reception, and access to event space and boardrooms.",
  },
  {
    name: "Private Office",
    slug: "private-offices",
    description: "A private office is a furnished room reserved for one business. It gives that team privacy and control over the space, and typically includes high-speed internet, meeting rooms, reception, and office equipment.",
  },
  {
    name: "Shared Office",
    slug: "shared-offices",
    description: "A shared office is a desk or private room inside another business's leased office. The tenant sublets unused space so both sides share rent and utilities, without you taking on a long-term lease.",
  },
  {
    name: "Sublease Office",
    slug: "conventional-sublet-offices",
    description: "A sublease office is commercial space a company is leaving before its lease ends. It is a lower-cost way to take a traditional office, often already fitted out, with room to negotiate a shorter term.",
  },
  {
    name: "Virtual Office",
    slug: "virtual-office",
    description: "A virtual office gives a business a professional presence without a full-time physical office. It can include a business address, mail handling, a telephone answering service, meeting rooms, and administrative support.",
  },
] as const;

function officeServices(regionPrefix: string, countryName: string, providerId: string) {
  return OFFICE_SERVICES.map((service) => {
    const url = `${SITE_ORIGIN}${regionPrefix}/office-space/${service.slug}`;
    return {
      "@type": "Service",
      "@id": `${url}#service`,
      name: service.name,
      serviceType: service.name,
      description: service.description,
      url,
      areaServed: { "@type": "Country", name: countryName },
      provider: { "@id": providerId },
    };
  });
}

const ORGANIZATION_LOGO = {
  "@type": "ImageObject",
  url: DEFAULT_LOGO_URL,
  width: 400,
  height: 400,
};

const HQ_ADDRESS = {
  "@type": "PostalAddress",
  streetAddress: "1301/109 Pitt St",
  addressLocality: "Sydney",
  addressRegion: "NSW",
  postalCode: "2000",
  addressCountry: "AU",
};

/** Same node on every regional homepage. Regional desks use their own @id. */
const HEADQUARTERS = {
  "@type": "Organization",
  "@id": ORGANIZATION_ID,
  name: "Office Hub",
  legalName: LEGAL_NAME,
  url: SITE_ORIGIN,
  email: HQ_EMAIL,
  telephone: HQ_PHONE,
  address: HQ_ADDRESS,
  logo: ORGANIZATION_LOGO,
  image: ORGANIZATION_LOGO,
  sameAs: [
    "https://www.facebook.com/officehubworkspaces",
    "https://twitter.com/officehubtweet",
    "https://www.pinterest.com/officehubaus",
    "https://www.linkedin.com/company/office-hub-pty-ltd",
    "https://www.youtube.com/channel/UCt4FPeBFmHtejAWDVk0J-4A",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    telephone: HQ_PHONE,
    email: HQ_EMAIL,
    contactType: "Customer Service",
    availableLanguage: "en",
  },
};

interface CountryMetaData {
  country: string;
  region?: string;
}

interface RegionAddress {
  streetAddress?: string;
  addressLocality?: string;
  addressRegion?: string;
  postalCode?: string;
  addressCountry?: string;
}

interface FeaturedProperty {
  id?: string | number;
  name?: string;
  buildingName?: string;
  addressFormula?: string;
  imageUrl?: string;
  country?: string;
  city?: string;
  state?: string;
  streetName?: string;
  streetNumber?: string;
  zipcode?: string | number;
  hideStreetNumber?: boolean;
  assets?: Array<{ image?: string; url?: string }>;
}

interface RegionalOfficeConfig {
  name: string;
  countryName: string;
  iso2: string;
  email?: string;
  telephone?: string;
  address?: typeof HQ_ADDRESS;
}

interface HomeMetaData {
  country?: CountryMetaData | string;
  region?: string;
  nearByProperties?: FeaturedProperty[] | { results?: FeaturedProperty[] };
}

function resolveHomeMetaImage(data: HomeMetaData) {
  const firstProperty = asList(data?.nearByProperties)[0];
  const rawImage =
    firstProperty?.assets?.[0]?.image ||
    firstProperty?.assets?.[0]?.url ||
    firstProperty?.imageUrl;

  if (!rawImage) {
    return { imageUrl: DEFAULT_LOGO_URL, imageWidth: 400, imageHeight: 400 };
  }

  const imageUrl = rawImage.startsWith("http")
    ? rawImage
    : `https://ik.imagekit.io/officehub/tr:w-1200,h-628,c-maintain_ratio,f-auto/${rawImage}`;

  return { imageUrl, imageWidth: 1200, imageHeight: 628 };
}

function asList<T>(value: T[] | { results?: T[] } | null | undefined): T[] {
  if (Array.isArray(value)) return value;
  if (value && Array.isArray(value.results)) return value.results;
  return [];
}

function absoluteSiteUrl(path: string) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${SITE_ORIGIN}${path.startsWith("/") ? path : `/${path}`}`;
}

/** ISO 3166-1 alpha-2. The UK region code is `uk`; the country code Google accepts is `GB`. */
function schemaCountryCode(region?: string) {
  const code = (region || "us").toLowerCase();
  if (code === "uk") return "GB";
  return code.toUpperCase();
}

function text(value: unknown) {
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null || value === "") return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value as object).length === 0;
  return false;
}

/** Drops undefined, null, "", and empty arrays/objects from a JSON-LD graph. */
function stripEmpty<T>(value: T): T {
  if (Array.isArray(value)) {
    return value
      .map((item) => stripEmpty(item))
      .filter((item) => !isEmptyValue(item)) as T;
  }
  if (value && typeof value === "object") {
    const cleaned: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
      if (isEmptyValue(entry)) continue;
      const next = stripEmpty(entry);
      if (isEmptyValue(next)) continue;
      cleaned[key] = next;
    }
    return cleaned as T;
  }
  return value;
}

function postalAddress(address: RegionAddress | undefined, countryCode: string) {
  if (!text(address?.streetAddress)) return undefined;
  return {
    "@type": "PostalAddress" as const,
    streetAddress: text(address?.streetAddress),
    addressLocality: text(address?.addressLocality),
    addressRegion: text(address?.addressRegion),
    postalCode: text(address?.postalCode),
    addressCountry: countryCode,
  };
}

/**
 * One record per region. Address is included only for a real office street.
 * Australia uses the headquarters address object. The UAE has no office
 * address, phone, or dedicated inbox in config (the India group email is not used).
 */
const REGIONAL_OFFICES: Record<string, RegionalOfficeConfig> = Object.fromEntries(
  Object.keys(regionConfigs).map((regionCode) => {
    const country = getCountryData(regionCode) as ReturnType<typeof getCountryData> & {
      address?: RegionAddress;
    };
    const iso2 = schemaCountryCode(regionCode);
    const telephone = text(country.phoneNumberWithCode);
    const email = regionCode === "ae" ? "" : text(getRegionEmail(regionCode));
    const address = regionCode === "au" ? HQ_ADDRESS : postalAddress(country.address, iso2);
    return [regionCode, {
      name: `Office Hub ${country.country}`,
      countryName: country.country,
      iso2,
      ...(email ? { email } : {}),
      ...(telephone ? { telephone } : {}),
      ...(address ? { address } : {}),
    }];
  }),
);

function contactPoint(email?: string, telephone?: string) {
  if (!email && !telephone) return undefined;
  return {
    "@type": "ContactPoint",
    ...(telephone ? { telephone } : {}),
    ...(email ? { email } : {}),
    contactType: "Customer Service",
    availableLanguage: "en",
  };
}

function propertyPostalAddress(property: FeaturedProperty) {
  const streetAddress = [
    property.hideStreetNumber ? "" : text(property.streetNumber),
    text(property.streetName),
  ].filter(Boolean).join(" ");
  const address = {
    "@type": "PostalAddress" as const,
    streetAddress,
    addressLocality: text(property.city),
    addressRegion: text(property.state),
    postalCode: text(property.zipcode),
    addressCountry: text(property.country),
  };
  const hasAddress = [
    address.streetAddress,
    address.addressLocality,
    address.addressRegion,
    address.postalCode,
    address.addressCountry,
  ].some(Boolean);
  return hasAddress ? address : undefined;
}

function listItem(
  position: number,
  name: string,
  url: string,
  image?: string,
  address?: ReturnType<typeof propertyPostalAddress>,
) {
  return {
    "@type": "ListItem",
    position,
    item: {
      "@type": "Place",
      name,
      url,
      ...(image ? { image } : {}),
      ...(address ? { address } : {}),
    },
  };
}

function featuredPropertyList(properties: FeaturedProperty[], countryLabel: string, pageUrl: string) {
  const items = properties.slice(0, FEATURED_PROPERTY_LIMIT).flatMap((property, index) => {
    const name = property.buildingName || property.name || property.addressFormula;
    const url = absoluteSiteUrl(propertySlug(property as Parameters<typeof propertySlug>[0]));
    if (!name || !url) return [];
    const rawImage = property.assets?.[0]?.image || property.assets?.[0]?.url || property.imageUrl;
    const image = rawImage
      ? (rawImage.startsWith("http") ? rawImage : absoluteSiteUrl(rawImage))
      : undefined;
    return [listItem(index + 1, name, url, image, propertyPostalAddress(property))];
  }).map((item, index) => ({ ...item, position: index + 1 }));

  if (items.length === 0) return null;
  return {
    "@type": "ItemList",
    "@id": `${pageUrl}/#featured-properties`,
    name: `Top Office Locations in ${countryLabel}`,
    numberOfItems: items.length,
    itemListElement: items,
  };
}

export const homeMeta = ({ data, path }: { data: HomeMetaData, path: string }) => {
  const countryData = data?.country || { country: DEFAULT_COUNTRY };
  const countryMeta = data.region ? getCountrySpecificMeta(data?.region) : null;
  const title = countryMeta?.metaTitle || `Find Flexible Coworking, Private and Shared Office Spaces Across ${getFormattedCountryName(data.region || "us")} – Office Hub `;
  const description = countryMeta?.metaDescription || `Explore coworking, private, serviced and managed offices across ${getFormattedCountryName(data.region || "us")}. Compare flexible office options nationwide and find your perfect workspace with Office Hub.`;
  const canonicalUrl = `${GetDomainName()}`;
  // Region-aware page URL so each regional homepage (/au, /sg, ...) advertises a
  // unique og:url. Social scrapers (e.g. LinkedIn) dedupe/cache by og:url, so a
  // shared root URL caused every region to surface the US homepage title.
  const countryName = typeof countryData === "string" ? countryData : countryData.country;
  const regionPrefix = getRegionUrlPrefix(countryName);
  const ogUrl = `${canonicalUrl}${regionPrefix}`;
  const regionCode = (data.region || "us").toLowerCase();
  const office = REGIONAL_OFFICES[regionCode] || REGIONAL_OFFICES.us;
  const { imageUrl, imageWidth, imageHeight } = resolveHomeMetaImage(data);
  const countryLabel = getFormattedCountryName(data.region || "us");
  const featuredProperties = asList(data.nearByProperties);
  const pageUrl = `${SITE_ORIGIN}${regionPrefix}`;
  const propertyList = featuredProperties.length > 3
    ? featuredPropertyList(featuredProperties, countryLabel, pageUrl)
    : null;
  const regionalOfficeId = `${SITE_ORIGIN}/#office-${regionCode}`;
  const services = officeServices(regionPrefix, office.countryName, regionalOfficeId);
  const regionalContact = contactPoint(office.email, office.telephone);

  const regionalOffice = {
    "@type": "Organization",
    "@id": regionalOfficeId,
    name: office.name,
    url: pageUrl,
    parentOrganization: { "@id": ORGANIZATION_ID },
    logo: ORGANIZATION_LOGO,
    image: ORGANIZATION_LOGO,
    areaServed: { "@type": "Country", name: office.countryName },
    sameAs: HEADQUARTERS.sameAs,
    ...(office.address ? { address: office.address } : {}),
    ...(office.telephone ? { telephone: office.telephone } : {}),
    ...(regionalContact ? { contactPoint: regionalContact } : {}),
  };

  const graph = stripEmpty([
    HEADQUARTERS,
    regionalOffice,
    ...services,
    {
      "@type": "WebPage",
      "@id": `${pageUrl}#webpage`,
      url: pageUrl,
      name: title,
      isPartOf: { "@id": WEBSITE_ID },
      about: { "@id": regionalOfficeId },
      inLanguage: `en-${office.iso2}`,
    },
    {
      "@type": "WebSite",
      "@id": WEBSITE_ID,
      name: "Office Hub",
      url: SITE_ORIGIN,
      publisher: { "@id": ORGANIZATION_ID },
    },
    ...(propertyList ? [propertyList] : []),
  ]);

  const ldJson = [
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@graph": graph,
      },
    },
  ];
  return [
    ...getCanonicalAndAlternates(canonicalUrl, path),
    ...getMetaOgTags({ title, description, imageUrl, canonicalUrl: ogUrl, imageWidth, imageHeight }),
    ...ldJson
  ];
};

